import React, { Fragment } from 'react';
import PropTypes from 'prop-types';
import { Dialog, Transition } from '@headlessui/react';
import { RiCloseFill } from 'react-icons/ri';
import clsx from 'clsx';
import { t } from 'ttag';

function Modal({ isOpen, onClose, title, children, maxWidth }) {
  return (
    <Transition show={isOpen} as={Fragment}>
      <Dialog className="relative z-[150]" onClose={() => onClose()}>
        <Transition.Child
          as={Fragment}
          enter="ease-out duration-300"
          enterFrom="opacity-0"
          enterTo="opacity-100"
          leave="ease-in duration-200"
          leaveFrom="opacity-100"
          leaveTo="opacity-0"
        >
          <div className="fixed inset-0 bg-black/30" />
        </Transition.Child>
        <Transition.Child
          as={Fragment}
          enter="ease-out duration-300"
          enterFrom="opacity-0 scale-95"
          enterTo="opacity-100 scale-100"
          leave="ease-in duration-200"
          leaveFrom="opacity-100 scale-100"
          leaveTo="opacity-0 scale-95"
        >
          <div className="fixed inset-0 overflow-y-auto">
            <div className="flex min-h-full items-center justify-center p-4">
              <Dialog.Panel
                className={clsx(
                  'relative mx-auto space-y-4 bg-white p-8 shadow-lg',
                  {
                    'max-w-sm': maxWidth === 'sm',
                    'max-w-md': maxWidth === 'md',
                    'max-w-lg': maxWidth === 'lg',
                    'max-w-xl': maxWidth === 'xl'
                  }
                )}
                style={{
                  maxWidth: typeof maxWidth === 'number' ? maxWidth : ''
                }}
              >
                <button
                  className="absolute right-0 top-0 m-0 flex cursor-pointer items-center border-0 bg-green p-2 text-white focus:outline-none"
                  type="button"
                  aria-label={t`Close Modal`}
                  onClick={() => onClose()}
                >
                  <RiCloseFill className="h-6 w-6 shrink-0" />
                </button>
                <Dialog.Title className="m-0 text-lg font-semibold text-night-rider">
                  {title}
                </Dialog.Title>
                {children}
              </Dialog.Panel>
            </div>
          </div>
        </Transition.Child>
      </Dialog>
    </Transition>
  );
}

Modal.propTypes = {
  /**
   * Determines whether the Modal is open or not.
   */
  isOpen: PropTypes.bool.isRequired,
  /**
   * Function to call when the Modal is closed. Usually used to set the `isOpen` property to `false`.
   */
  onClose: PropTypes.func.isRequired,
  /**
   * Determines the title of the Modal.
   */
  title: PropTypes.string.isRequired,
  /**
   * Determines the content of the Modal.
   */
  children: PropTypes.node,
  /**
   * Defines the Maximum Width of the USP List component. Can be one of the following: `sm`, `md`, `lg`, `xl` or a number in pixels.
   */
  maxWidth: PropTypes.oneOfType([
    PropTypes.oneOf(['sm', 'md', 'lg', 'xl']),
    PropTypes.number
  ])
};

Modal.defaultProps = {
  children: null,
  maxWidth: undefined
};

export default Modal;
